import { TranslateService } from '@ngx-translate/core';
import { AppInjector } from '../app.injector';
import { ParseIconsConfig, ParseIconsGlyph } from './parse-icons.config';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Replace a run of one glyph with its configured markup.
 *
 * Runs are handled together rather than one character at a time so a sequence can be announced
 * once and meaningfully — "Danger rating 3" — instead of repeating the same label per icon.
 */
export const replaceGlyphRuns = (content: string, glyph: string, def: ParseIconsGlyph) => {
    const runs = new RegExp(`(?:${escapeRegExp(glyph)})+`, 'gu');

    return content.replace(runs, (match) => {
        // Spread, not `.length`: these are frequently astral characters (💀 is a surrogate pair),
        // where `.length` counts code units and would double the count.
        const count = [...match].length;
        const body = def.html.repeat(count);

        if (!def.groupClass && !def.groupLabel) {
            return body;
        }

        const cls = def.groupClass ? ` class="${def.groupClass}"` : '';
        const label = def.groupLabel ? ` aria-label="${def.groupLabel} ${count}"` : '';
        return `<span${cls}${label}>${body}</span>`;
    });
};

export const parseIcons = (content: string, gameId?: string) => {
    if (!content) {
        return '';
    }
    let translateService = AppInjector.get(TranslateService);
    let parseIconsConfig = AppInjector.get(ParseIconsConfig);

    const folder = gameId ? `assets/${gameId}/images/icons` : parseIconsConfig.iconsFolder;
    // Pass through the name unchanged if it already has an extension; otherwise default to .png.
    const ext = (name: string) => (/\.[a-z]+$/i.test(name) ? name : `${name}.png`);
    const gameIcons = gameId ? parseIconsConfig.icons?.[gameId] : undefined;
    // Use a registered HTML override when present; fall back to <img> for unregistered icons.
    const renderIcon = (name: string, alt: string, sizeClass: string): string => {
        const def = gameIcons?.[name];
        if (def) return def.html.replace('{sizeClass}', sizeClass).replace('{alt}', alt);
        return `<img src="${folder}/${ext(name)}" alt="${alt}" title="${alt}" class="${sizeClass}" />`;
    };
    content = content.replace(/\{\{(.*?)\}\}/g, (_match, grp1) => translateService.instant(grp1));
    content = content
        .replace(/\[\[(.*?)\|(.*?)\|(tiny|small|medium|large|vLarge|auto)\]\]/g, (_, name, alt, size) =>
            renderIcon(name, alt, `${size}Icon`)
        )
        .replace(/\[\[(.*?)\|(tiny|small|medium|large|vLarge|auto)\]\]/g, (_, name, size) => renderIcon(name, '', `${size}Icon`))
        .replace(/\[\[(.*?)\|(.*?)\]\]/g, (_, name, alt) => renderIcon(name, alt, 'mediumIcon'))
        .replace(/\[\[(.*?)\]\]/g, (_, name) => renderIcon(name, '', 'mediumIcon'));

    // Applied last so the emitted markup cannot be re-matched by the bracket syntax above.
    const glyphs = gameId ? parseIconsConfig.glyphs?.[gameId] : undefined;
    if (glyphs) {
        for (const [glyph, def] of Object.entries(glyphs)) {
            content = replaceGlyphRuns(content, glyph, def);
        }
    }

    return content;
};
